import {LanguagesType} from '@/features/country/types'
import {Lens} from '@dhmk/zustand-lens'
import {GetTranslationRequest} from '../types'

type GlobalTranslateState = {
    userID?: string
    translateToggle: boolean
    currentLanguage?: LanguagesType
    requests: TranslationRequest[]
    results: Record<TranslationRequest['id'], GetTranslationRequest>
}

type GlobalTranslateActions = {
    overturnToggleSwitch: () => void
    initialTranslateConfig: (props: GlobalOptionalTranslateState) => void
    addRequest: (data: TranslationRequest) => void
    removeRequest: (requestId: TranslationRequest['id']) => void
    setResults: (results: GetTranslationRequest[]) => void
    clearRequests: () => void
    clearResults: () => void
}

export type TranslationRequest = {
    id: string
    text: string
    targetLang: string
}

export type GlobalTranslateModal = GlobalTranslateState & GlobalTranslateActions

const initialModal: GlobalTranslateState = {
    translateToggle: false,
    requests: [],
    results: {}
}

const createGlobalTranslateSlice: Lens<GlobalTranslateModal> = set => ({
    ...initialModal,
    addRequest: request =>
        set(state => {
            if (state.requests.some(req => req.id === request.id)) {
                return state
            }
            return {requests: [...state.requests, request]}
        }),
    removeRequest: requestId => set(state => ({requests: state.requests.filter(request => request.id !== requestId)})),
    setResults: results =>
        set(state => {
            const newResults: Record<string, GetTranslationRequest> = {...state.results}
            results.forEach(item => {
                newResults[item.id] = item
            })
            return {results: newResults}
        }),
    clearRequests: () => set({requests: initialModal.requests}),
    clearResults: () => set({results: initialModal.results}),

    overturnToggleSwitch: () =>
        set(state => {
            state.initialTranslateConfig({translateToggle: !state.translateToggle})
            return {translateToggle: !state.translateToggle}
        }),
    initialTranslateConfig: props =>
        set(state =>
            initialGlobalTranslateInLocalStorage({
                userID: state.userID,
                ...props
            })
        )
})
export default createGlobalTranslateSlice
const GLOBAL_TRANSLATION_IN_LOCAL_STORAGE_KEY_MAP = {
    translation: 'translation'
}

type GlobalOptionalTranslateState = {
    userID?: string
    translateToggle?: boolean
    currentLanguage?: LanguagesType
}

const initialGlobalTranslateInLocalStorage = (props: GlobalOptionalTranslateState) => {
    const {userID, ...extraProps} = props

    const prevTranslationConfig = localStorage.getItem(GLOBAL_TRANSLATION_IN_LOCAL_STORAGE_KEY_MAP.translation) || '{}'
    const parsePrevTranslationConfig = JSON.parse(prevTranslationConfig)

    const translateConfig = Object.assign(parsePrevTranslationConfig, {
        [String(userID)]: {...parsePrevTranslationConfig[String(userID)], ...extraProps}
    })

    localStorage.setItem(GLOBAL_TRANSLATION_IN_LOCAL_STORAGE_KEY_MAP.translation, JSON.stringify(translateConfig))

    return {userID, ...translateConfig[String(userID)]}
}
